import type { ApprovedFormulaDefinition, ApprovedFormulaResult } from './approved-formulas';
import {
  ORG_ANALYSIS_APPROVED_FORMULA_DEFINITIONS,
  type OrgAnalysisSubstrateAnchor,
  type OrgTimeToRealizeInput,
  type OrgTimeToRealizeOutput,
} from './org-analysis-approved-formulas';
import {
  sourceClassPackagePosture,
  type DecisionAgentSourceClass,
} from './contracts/sourceClasses';
import {
  addExecutionDays,
  hasExactKeyCount,
  isNonEmptyText as isText,
  isRecord,
  sameStringSet,
} from './org-execution-chain-validation';
import {
  isSynergyStreamLinkRecord,
  validateSynergyStreamLinkReceipts,
} from './org-execution-chain-synergy-receipts';
import { buildOrgTimeToRealizeSchedule } from './org-time-to-realize-schedule';

export const ORG_EXECUTION_CHAIN_SCHEMA_VERSION = 'org_execution_chain_v1';

type ModeledSource = Readonly<{ sourceClass: 'modeled'; actualClientData: false }>;
type ClientSource = Readonly<{
  sourceClass: Exclude<
    DecisionAgentSourceClass,
    'modeled' | 'synthetic-demo' | 'extrapolated' | 'industry_reference'
  >;
  actualClientData: true;
}>;
type ArtifactSource = ModeledSource | ClientSource;

export type ExecutionChainReferenceCatalogV1 = Readonly<{
  roleIds: readonly string[];
  capabilityIds: readonly string[];
  canonicalInitiativeIds: readonly string[];
  initiatives: readonly Readonly<{
    initiativeId: string;
    startDate: string;
    endDate: string;
  }>[];
  synergyStreams: readonly Readonly<{
    synergyClaimId: string;
    overlapReceiptStatus: 'approved' | 'pending' | 'rejected';
  }>[];
}>;

export type OrgExecutionChainArtifactV1 = ArtifactSource &
  Readonly<{
    schemaVersion: typeof ORG_EXECUTION_CHAIN_SCHEMA_VERSION;
    artifactId: string;
    reportingCutAsOf: string;
    formulaBinding: Readonly<{
      formulaId: 'org_time_to_realize_v1';
      formulaVersion: '1.0.0';
      optionId: string;
      optionVersion: number;
    }>;
    workPackages: readonly (ArtifactSource &
      Readonly<{
        workPackageId: string;
        predecessorIds: readonly string[];
        durationDays: number;
        ownerRoleId: string;
        capabilityIds: readonly string[];
        criticalRoleAssignmentIds: readonly string[];
        milestoneId: string;
        initiativeLinkId: string;
        synergyStreamLinkIds: readonly string[];
        startsBenefit: boolean;
        requiredForFullRunRate: boolean;
        cumulativeRunRateFraction: number | null;
      }>)[];
    milestones: readonly (ArtifactSource &
      Readonly<{
        milestoneId: string;
        workPackageId: string;
        milestoneDate: string;
      }>)[];
    criticalRoleAssignments: readonly (ArtifactSource &
      Readonly<{
        roleAssignmentId: string;
        workPackageId: string;
        roleId: string;
      }>)[];
    initiativeLinks: readonly (ArtifactSource &
      Readonly<{
        initiativeLinkId: string;
        workPackageId: string;
        initiativeId: string;
      }>)[];
    initiativeCrosswalks: readonly (ArtifactSource &
      Readonly<{
        subInitiativeId: string;
        canonicalInitiativeId: string;
      }>)[];
    synergyStreamLinks: readonly (ArtifactSource &
      Readonly<{
        synergyStreamLinkId: string;
        workPackageId: string;
        synergyClaimId: string;
      }> &
      (
        | Readonly<{ aggregationDisposition: 'withheld_pending_overlap_receipts' }>
        | Readonly<{ aggregationDisposition: 'withdrawn_overlap_rejected' }>
        | Readonly<{
            aggregationDisposition: 'released_with_overlap_receipt';
            overlapReceiptId: string;
          }>
      ))[];
  }>;

export type ExecutionChainValidationResult =
  | Readonly<{ ok: true; value: OrgExecutionChainArtifactV1 }>
  | Readonly<{ ok: false; errors: readonly string[] }>;

export type ExecutionChainFormulaContext = Readonly<{
  substrateAnchor: OrgAnalysisSubstrateAnchor;
  evidenceRefIds: readonly string[];
  reportingCutAsOf: string;
}>;

export type ExecutionChainFormulaExecutionResult =
  | Readonly<{ status: 'invalid_execution_chain'; errors: readonly string[] }>
  | Readonly<{
      status: 'ok';
      formulaInput: OrgTimeToRealizeInput;
      formulaResult: ApprovedFormulaResult<OrgTimeToRealizeOutput>;
      withheldSynergyClaimIds: readonly string[];
    }>;

export function parseExecutionChainArtifactV1(
  raw: unknown,
  catalog: ExecutionChainReferenceCatalogV1
): ExecutionChainValidationResult {
  const artifact = rawArtifactOrNull(raw);
  if (!artifact) return invalid(['artifact must be a complete org_execution_chain_v1 record']);
  const errors = validateArtifactShape(artifact);
  validateIdentityReferences(artifact, catalog, errors);
  validateWorkPackageLinks(artifact, errors);
  validateSchedule(artifact, catalog, errors);
  return errors.length === 0 ? Object.freeze({ ok: true, value: artifact }) : invalid(errors);
}

export function executeExecutionChainTimeToRealize(
  raw: unknown,
  catalog: ExecutionChainReferenceCatalogV1,
  context: ExecutionChainFormulaContext
): ExecutionChainFormulaExecutionResult {
  const parsed = parseExecutionChainArtifactV1(raw, catalog);
  if (!parsed.ok)
    return Object.freeze({ status: 'invalid_execution_chain', errors: parsed.errors });
  if (parsed.value.reportingCutAsOf !== context.reportingCutAsOf) {
    return Object.freeze({
      status: 'invalid_execution_chain',
      errors: Object.freeze(['execution-chain reporting cut does not match formula context']),
    });
  }
  const formulaInput = toOrgTimeToRealizeInput(parsed.value, context);
  const formula = timeToRealizeFormula();
  return Object.freeze({
    status: 'ok',
    formulaInput,
    formulaResult: formula.execute(formulaInput),
    withheldSynergyClaimIds: Object.freeze(
      parsed.value.synergyStreamLinks.map((link) => link.synergyClaimId).sort()
    ),
  });
}

export function toOrgTimeToRealizeInput(
  artifact: OrgExecutionChainArtifactV1,
  context: ExecutionChainFormulaContext
): OrgTimeToRealizeInput {
  return Object.freeze({
    substrateAnchor: context.substrateAnchor,
    evidenceRefIds: Object.freeze([...context.evidenceRefIds]),
    optionId: artifact.formulaBinding.optionId,
    optionVersion: artifact.formulaBinding.optionVersion,
    reportingCutAsOf: artifact.reportingCutAsOf,
    workPackages: Object.freeze(artifact.workPackages.map(toFormulaWorkPackage)),
  });
}

function toFormulaWorkPackage(workPackage: OrgExecutionChainArtifactV1['workPackages'][number]) {
  return Object.freeze({
    workPackageId: workPackage.workPackageId,
    predecessorIds: Object.freeze([...workPackage.predecessorIds]),
    durationDays: workPackage.durationDays,
    ownerRoleId: workPackage.ownerRoleId,
    startsBenefit: workPackage.startsBenefit,
    requiredForFullRunRate: workPackage.requiredForFullRunRate,
    cumulativeRunRateFraction: workPackage.cumulativeRunRateFraction,
  });
}

function timeToRealizeFormula(): ApprovedFormulaDefinition<
  OrgTimeToRealizeInput,
  OrgTimeToRealizeOutput
> {
  const formula = ORG_ANALYSIS_APPROVED_FORMULA_DEFINITIONS.find(
    (definition) => definition.formulaId === 'org_time_to_realize_v1'
  );
  if (!formula) throw new Error('Approved org_time_to_realize_v1 formula is unavailable');
  return formula as ApprovedFormulaDefinition<OrgTimeToRealizeInput, OrgTimeToRealizeOutput>;
}

function rawArtifactOrNull(raw: unknown): OrgExecutionChainArtifactV1 | null {
  if (
    !isRecord(raw) ||
    !hasExactKeyCount(raw, 12) ||
    raw.schemaVersion !== ORG_EXECUTION_CHAIN_SCHEMA_VERSION ||
    !isExecutionChainSource(raw)
  ) {
    return null;
  }
  if (
    !isText(raw.artifactId) ||
    !isText(raw.reportingCutAsOf) ||
    !isFormulaBinding(raw.formulaBinding)
  ) {
    return null;
  }
  if (!isArrayOfRecords(raw.workPackages, isWorkPackageRecord)) return null;
  if (!isArrayOfRecords(raw.milestones, isMilestoneRecord)) return null;
  if (!isArrayOfRecords(raw.criticalRoleAssignments, isRoleAssignmentRecord)) return null;
  if (!isArrayOfRecords(raw.initiativeLinks, isInitiativeLinkRecord)) return null;
  if (!isArrayOfRecords(raw.initiativeCrosswalks, isInitiativeCrosswalkRecord)) return null;
  if (!isArrayOfRecords(raw.synergyStreamLinks, isSynergyStreamLinkRecord)) return null;
  return raw as OrgExecutionChainArtifactV1;
}

function isWorkPackageRecord(value: unknown): value is Record<string, unknown> {
  return (
    isRecord(value) &&
    hasExactKeyCount(value, 14) &&
    isExecutionChainSource(value) &&
    isText(value.workPackageId) &&
    uniqueText(value.predecessorIds) &&
    isPositiveInteger(value.durationDays) &&
    isText(value.ownerRoleId) &&
    nonEmptyUniqueText(value.capabilityIds) &&
    nonEmptyUniqueText(value.criticalRoleAssignmentIds) &&
    isText(value.milestoneId) &&
    isText(value.initiativeLinkId) &&
    nonEmptyUniqueText(value.synergyStreamLinkIds) &&
    typeof value.startsBenefit === 'boolean' &&
    typeof value.requiredForFullRunRate === 'boolean' &&
    (value.cumulativeRunRateFraction === null ||
      (typeof value.cumulativeRunRateFraction === 'number' &&
        Number.isFinite(value.cumulativeRunRateFraction) &&
        value.cumulativeRunRateFraction >= 0 &&
        value.cumulativeRunRateFraction <= 1))
  );
}

function isMilestoneRecord(value: unknown): value is Record<string, unknown> {
  return (
    isRecord(value) &&
    hasExactKeyCount(value, 5) &&
    isExecutionChainSource(value) &&
    isText(value.milestoneId) &&
    isText(value.workPackageId) &&
    isText(value.milestoneDate)
  );
}

function isRoleAssignmentRecord(value: unknown): value is Record<string, unknown> {
  return (
    isRecord(value) &&
    hasExactKeyCount(value, 5) &&
    isExecutionChainSource(value) &&
    isText(value.roleAssignmentId) &&
    isText(value.workPackageId) &&
    isText(value.roleId)
  );
}

function isInitiativeLinkRecord(value: unknown): value is Record<string, unknown> {
  return (
    isRecord(value) &&
    hasExactKeyCount(value, 5) &&
    isExecutionChainSource(value) &&
    isText(value.initiativeLinkId) &&
    isText(value.workPackageId) &&
    isText(value.initiativeId)
  );
}

function isInitiativeCrosswalkRecord(value: unknown): value is Record<string, unknown> {
  return (
    isRecord(value) &&
    hasExactKeyCount(value, 4) &&
    isExecutionChainSource(value) &&
    isText(value.subInitiativeId) &&
    isText(value.canonicalInitiativeId)
  );
}

function isArrayOfRecords(
  value: unknown,
  itemGuard: (item: unknown) => item is Record<string, unknown>
): value is readonly Record<string, unknown>[] {
  return Array.isArray(value) && value.length > 0 && value.every(itemGuard);
}

function validateArtifactShape(artifact: OrgExecutionChainArtifactV1): string[] {
  const errors: string[] = [];
  if (!isIsoDate(artifact.reportingCutAsOf)) errors.push('reportingCutAsOf must be an ISO date');
  validateUnique(artifact.workPackages, 'workPackageId', errors);
  validateUnique(artifact.milestones, 'milestoneId', errors);
  validateUnique(artifact.criticalRoleAssignments, 'roleAssignmentId', errors);
  validateUnique(artifact.initiativeLinks, 'initiativeLinkId', errors);
  validateUnique(artifact.initiativeCrosswalks, 'subInitiativeId', errors);
  validateUnique(artifact.synergyStreamLinks, 'synergyStreamLinkId', errors);
  return errors;
}

function validateIdentityReferences(
  artifact: OrgExecutionChainArtifactV1,
  catalog: ExecutionChainReferenceCatalogV1,
  errors: string[]
): void {
  const roleIds = new Set(catalog.roleIds);
  const capabilityIds = new Set(catalog.capabilityIds);
  const initiativeIds = new Set(catalog.initiatives.map((initiative) => initiative.initiativeId));
  const canonicalInitiativeIds = new Set(catalog.canonicalInitiativeIds);
  const streamsById = new Map(
    catalog.synergyStreams.map((stream) => [stream.synergyClaimId, stream])
  );
  for (const assignment of artifact.criticalRoleAssignments) {
    if (!roleIds.has(assignment.roleId)) errors.push(`orphan role id: ${assignment.roleId}`);
  }
  for (const link of artifact.initiativeLinks) {
    if (!initiativeIds.has(link.initiativeId))
      errors.push(`orphan initiative id: ${link.initiativeId}`);
  }
  for (const workPackage of artifact.workPackages)
    for (const capabilityId of workPackage.capabilityIds)
      if (!capabilityIds.has(capabilityId)) errors.push(`orphan capability id: ${capabilityId}`);
  for (const crosswalk of artifact.initiativeCrosswalks)
    if (!canonicalInitiativeIds.has(crosswalk.canonicalInitiativeId))
      errors.push(`orphan canonical initiative id: ${crosswalk.canonicalInitiativeId}`);
  validateSynergyStreamLinkReceipts(artifact.synergyStreamLinks, streamsById, errors);
}

function validateWorkPackageLinks(artifact: OrgExecutionChainArtifactV1, errors: string[]): void {
  const workPackageIds = new Set(
    artifact.workPackages.map((workPackage) => workPackage.workPackageId)
  );
  const assignments = groupIds(
    artifact.criticalRoleAssignments,
    'workPackageId',
    'roleAssignmentId'
  );
  const milestones = groupIds(artifact.milestones, 'workPackageId', 'milestoneId');
  const initiatives = groupIds(artifact.initiativeLinks, 'workPackageId', 'initiativeLinkId');
  const streams = groupIds(artifact.synergyStreamLinks, 'workPackageId', 'synergyStreamLinkId');
  const assignmentById = new Map(
    artifact.criticalRoleAssignments.map((assignment) => [assignment.roleAssignmentId, assignment])
  );
  validateLinkedRowParents(artifact, workPackageIds, errors);
  if (
    !sameStringSet(
      artifact.initiativeLinks.map((link) => link.initiativeId),
      artifact.initiativeCrosswalks.map((crosswalk) => crosswalk.subInitiativeId)
    )
  ) {
    errors.push('initiative crosswalk does not exactly cover local initiatives');
  }
  for (const workPackage of artifact.workPackages) {
    if (!hasDeclaredOwnerAssignment(workPackage, assignmentById)) {
      errors.push(`owner lacks critical role assignment: ${workPackage.workPackageId}`);
    }
    validateListedLinks(workPackage, assignments, milestones, initiatives, streams, errors);
    for (const predecessorId of workPackage.predecessorIds) {
      if (!workPackageIds.has(predecessorId) || predecessorId === workPackage.workPackageId) {
        errors.push(`invalid predecessor ${predecessorId} for ${workPackage.workPackageId}`);
      }
    }
  }
}

function hasDeclaredOwnerAssignment(
  workPackage: OrgExecutionChainArtifactV1['workPackages'][number],
  assignmentById: ReadonlyMap<
    string,
    OrgExecutionChainArtifactV1['criticalRoleAssignments'][number]
  >
): boolean {
  return workPackage.criticalRoleAssignmentIds.some((assignmentId) => {
    const assignment = assignmentById.get(assignmentId);
    return (
      assignment?.workPackageId === workPackage.workPackageId &&
      assignment.roleId === workPackage.ownerRoleId
    );
  });
}

function validateListedLinks(
  workPackage: OrgExecutionChainArtifactV1['workPackages'][number],
  assignments: Map<string, string[]>,
  milestones: Map<string, string[]>,
  initiatives: Map<string, string[]>,
  streams: Map<string, string[]>,
  errors: string[]
): void {
  const workPackageId = workPackage.workPackageId;
  validateExactLinks(
    assignments,
    workPackageId,
    workPackage.criticalRoleAssignmentIds,
    'critical-role assignment',
    errors
  );
  validateExactLinks(milestones, workPackageId, [workPackage.milestoneId], 'milestone', errors);
  validateExactLinks(
    initiatives,
    workPackageId,
    [workPackage.initiativeLinkId],
    'initiative link',
    errors
  );
  validateExactLinks(
    streams,
    workPackageId,
    workPackage.synergyStreamLinkIds,
    'synergy link',
    errors
  );
}

function validateExactLinks(
  links: ReadonlyMap<string, readonly string[]>,
  workPackageId: string,
  expected: readonly string[],
  label: string,
  errors: string[]
): void {
  if (!sameStringSet(links.get(workPackageId) ?? [], expected))
    errors.push(`non-reciprocal ${label} membership: ${workPackageId}`);
}

function validateLinkedRowParents(
  artifact: OrgExecutionChainArtifactV1,
  workPackageIds: ReadonlySet<string>,
  errors: string[]
): void {
  const linkedRows = [
    ...artifact.milestones,
    ...artifact.criticalRoleAssignments,
    ...artifact.initiativeLinks,
    ...artifact.synergyStreamLinks,
  ];
  for (const row of linkedRows) {
    if (!workPackageIds.has(row.workPackageId)) {
      errors.push(`orphan work package id: ${row.workPackageId}`);
    }
  }
}

function validateSchedule(
  artifact: OrgExecutionChainArtifactV1,
  catalog: ExecutionChainReferenceCatalogV1,
  errors: string[]
): void {
  const schedule = buildOrgTimeToRealizeSchedule(artifact.workPackages);
  if (!schedule) {
    errors.push('work package dependencies contain a cycle');
    return;
  }
  const initiativesById = new Map(
    catalog.initiatives.map((initiative) => [initiative.initiativeId, initiative])
  );
  const initiativeByLinkId = new Map(
    artifact.initiativeLinks.map((link) => [link.initiativeLinkId, link.initiativeId])
  );
  const milestoneById = new Map(
    artifact.milestones.map((milestone) => [milestone.milestoneId, milestone])
  );
  for (const workPackage of artifact.workPackages) {
    const initiative = initiativesById.get(
      initiativeByLinkId.get(workPackage.initiativeLinkId) ?? ''
    );
    const milestone = milestoneById.get(workPackage.milestoneId);
    const expectedMilestoneDate = addExecutionDays(
      artifact.reportingCutAsOf,
      schedule.completionDays.get(workPackage.workPackageId) ?? -1
    );
    if (!initiative || !milestone || !isWithinWindow(milestone.milestoneDate, initiative)) {
      errors.push(`milestone is outside initiative window: ${workPackage.workPackageId}`);
    }
    if (milestone?.milestoneDate !== expectedMilestoneDate)
      errors.push(`milestone does not match derived schedule: ${workPackage.workPackageId}`);
  }
}

function groupIds<T extends Record<string, unknown>>(
  rows: readonly T[],
  groupKey: keyof T,
  idKey: keyof T
): Map<string, string[]> {
  const result = new Map<string, string[]>();
  for (const row of rows)
    result.set(String(row[groupKey]), [
      ...(result.get(String(row[groupKey])) ?? []),
      String(row[idKey]),
    ]);
  return result;
}

function validateUnique<T extends Record<string, unknown>>(
  rows: readonly T[],
  key: keyof T,
  errors: string[]
): void {
  const ids = rows.map((row) => row[key]);
  if (ids.some((id) => !isText(id)) || new Set(ids).size !== ids.length)
    errors.push(`duplicate or empty ${String(key)}`);
}

function isWithinWindow(
  date: string,
  initiative: ExecutionChainReferenceCatalogV1['initiatives'][number]
): boolean {
  return (
    isIsoDate(date) &&
    isIsoDate(initiative.startDate) &&
    isIsoDate(initiative.endDate) &&
    date >= initiative.startDate &&
    date <= initiative.endDate
  );
}

function isFormulaBinding(value: unknown): boolean {
  return (
    isRecord(value) &&
    hasExactKeyCount(value, 4) &&
    value.formulaId === 'org_time_to_realize_v1' &&
    value.formulaVersion === '1.0.0' &&
    isText(value.optionId) &&
    isPositiveInteger(value.optionVersion)
  );
}

export function isExecutionChainSource(
  value: Record<string, unknown>
): value is Record<string, unknown> & ArtifactSource {
  return (
    (value.sourceClass === 'modeled' && value.actualClientData === false) ||
    (value.actualClientData === true &&
      isText(value.sourceClass) &&
      sourceClassPackagePosture(value.sourceClass) === 'source_receipted')
  );
}

function nonEmptyUniqueText(value: unknown): value is readonly string[] {
  return Array.isArray(value) && value.length > 0 && uniqueText(value);
}

function uniqueText(value: unknown): value is readonly string[] {
  return Array.isArray(value) && value.every(isText) && new Set(value).size === value.length;
}

function isIsoDate(value: unknown): value is string {
  return (
    isText(value) &&
    /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    !Number.isNaN(Date.parse(`${value}T00:00:00.000Z`))
  );
}

function isPositiveInteger(value: unknown): value is number {
  return Number.isInteger(value) && typeof value === 'number' && value > 0;
}

function invalid(errors: readonly string[]): ExecutionChainValidationResult {
  return Object.freeze({ ok: false, errors: Object.freeze([...new Set(errors)]) });
}
